import React, { useEffect, useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';
import { Link, useLocation } from 'react-router-dom';
import { Tag, Typography, Divider, theme } from 'antd';
import { ESensorMode } from '@/types';
import {
  pluralize,
  computeHealthCounts,
  buildSensorBadgeConfigs,
  VERTICAL_DIVIDER_STYLE,
  SENSOR_TAG_STYLE,
} from '../shared/sensorUtils';
import { INTEGRATIONS_API_URL } from '@/api/integrations';
import WarningIcon from '@/components/Icons/Warning';
import { SENSOR_BADGE_ICONS, ICON_STYLE } from '../shared/sensorBadgeIcons';

// Self-contained query: all floors under the building with their sensors and
// health. Apollo dedupes shared fields with any parent BuildingPage query.
const BUILDING_SETUP_QUERY = gql`
  query BuildingSetupData($buildingId: ID!) {
    building(id: $buildingId) {
      id
      capacity {
        max
      }
      floors {
        id
        sensors {
          id: sensor_id
          mode
          isOnline: is_online
          isSleepTime: is_sleep_time
          deviceStatus: device_status
          connectionHealth: connection_health {
            status
          }
        }
      }
    }
  }
`;

// 3P sensors live in the integrations service. We aggregate across all floors
// in the building in a single round-trip by passing the full floor_ids list.
const BUILDING_SENSORS_3P_QUERY = gql`
  query BuildingSensors3p($floor_ids: [String!]) {
    sensors3p(floor_ids: $floor_ids) {
      id
      device_type
    }
  }
`;

interface BuildingSetupCardProps {
  buildingId: string;
}

const BuildingSetupCard: React.FC<BuildingSetupCardProps> = ({ buildingId }) => {
  const { search } = useLocation();
  const { token } = theme.useToken();

  const { data, error } = useQuery(BUILDING_SETUP_QUERY, {
    variables: { buildingId },
    skip: !buildingId,
  });
  // Surface query failures to the console so an outage / permission error
  // isn't silently invisible — the card itself returns null on error (see
  // below) because there's no expanded view to embed an inline message in,
  // matching the rest-of-row chrome that lives outside this component.
  useEffect(() => {
    if (error) {
      console.error('BuildingSetupCard query failed', { buildingId, error });
    }
  }, [buildingId, error]);

  const building = data?.building;
  const buildingCapacity: number = building?.capacity?.max ?? 0;

  const allSensors = useMemo(
    (): Array<{
      id: string;
      mode: string;
      isOnline: boolean;
      isSleepTime: boolean;
      deviceStatus?: 'ACTIVE' | 'ARCHIVED' | null;
      connectionHealth: { status: string };
    }> => (building?.floors ?? []).flatMap((f: { sensors?: unknown[] }) => f.sensors ?? []),
    [building],
  );

  const floorIds = useMemo(
    () => (building?.floors ?? []).map((f: { id: string }) => f.id),
    [building],
  );

  const { data: sensors3pData } = useQuery(BUILDING_SENSORS_3P_QUERY, {
    variables: { floor_ids: floorIds },
    skip: floorIds.length === 0,
    context: { uri: INTEGRATIONS_API_URL },
  });
  // Memoize to keep the empty-array identity stable while the query is in
  // flight — otherwise the `motionCount` / `deskCount` memos below
  // invalidate on every render until the first data tick lands.
  const sensors3p: Array<{ id: string; device_type: string }> = useMemo(
    () => sensors3pData?.sensors3p ?? [],
    [sensors3pData],
  );

  const trafficCount = useMemo(
    () => allSensors.filter((s) => s.mode === ESensorMode.TRAFFIC).length,
    [allSensors],
  );
  const presenceCount = useMemo(
    () => allSensors.filter((s) => s.mode === ESensorMode.PRESENCE).length,
    [allSensors],
  );
  const motionCount = useMemo(
    () => sensors3p.filter((s) => s.device_type === 'motion_sensor').length,
    [sensors3p],
  );
  const deskCount = useMemo(
    () => sensors3p.filter((s) => s.device_type === 'desk_sensor').length,
    [sensors3p],
  );

  const { offlineCount, warningCount } = useMemo(
    () => computeHealthCounts(allSensors),
    [allSensors],
  );
  const issueCount = offlineCount + warningCount;

  const sensorTypeBadges = useMemo(
    () =>
      buildSensorBadgeConfigs({
        traffic: trafficCount,
        presence: presenceCount,
        motion: motionCount,
        desk: deskCount,
      }),
    [trafficCount, presenceCount, motionCount, deskCount],
  );

  // Hide the card entirely while loading AND on error — the badge row has
  // no expanded state where an error message could surface (unlike
  // FloorSetupCard), so rendering a bare "Installed:" shell on error would
  // misrepresent the building as healthy with zero sensors.
  if (!building) return null;

  return (
    <div
      style={{
        paddingLeft: token.paddingLG,
        paddingRight: token.paddingLG,
        paddingTop: token.paddingXXS,
        paddingBottom: token.paddingXXS,
        backgroundColor: token.colorBgLayout,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: token.marginXS,
          flexWrap: 'wrap',
        }}
      >
        <Typography.Text type="secondary">Installed:</Typography.Text>
        {sensorTypeBadges.map((badge) => (
          <span
            key={badge.label}
            style={{ display: 'inline-flex', alignItems: 'center', gap: token.marginXXS }}
          >
            <Typography.Text>{badge.count} x</Typography.Text>
            <Tag color={badge.color} bordered={false} style={SENSOR_TAG_STYLE}>
              {SENSOR_BADGE_ICONS[badge.label]}
              {badge.label}
            </Tag>
          </span>
        ))}

        {buildingCapacity > 0 && (
          <>
            <Divider type="vertical" style={VERTICAL_DIVIDER_STYLE} />
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: token.marginXXS }}>
              <Typography.Text type="secondary">Capacity:</Typography.Text>
              <Typography.Text>{buildingCapacity}</Typography.Text>
            </span>
          </>
        )}

        {issueCount > 0 && <Divider type="vertical" style={VERTICAL_DIVIDER_STYLE} />}
        {issueCount > 0 && (
          <Link
            to={`/devices${search}`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: token.marginXXS,
              padding: `${token.paddingXXS}px ${token.paddingXS}px`,
              borderRadius: token.borderRadiusLG,
              fontSize: token.fontSizeSM,
              fontWeight: 500,
              backgroundColor: token.colorErrorBg,
              color: token.colorError,
              border: `1px solid ${token.colorErrorBorder}`,
            }}
          >
            <WarningIcon style={ICON_STYLE} />
            {offlineCount > 0 && `${pluralize(offlineCount, 'Sensor')} Offline`}
            {offlineCount > 0 && warningCount > 0 && ', '}
            {warningCount > 0 && `${warningCount} Warning`}
          </Link>
        )}
      </div>
    </div>
  );
};

export default BuildingSetupCard;
